/**
 * @fileoverview Input/output schemas for weakness-solutions-v1.
 *
 * This agent takes a single diagnosed weakness (produced by story-weaknesses-v1)
 * plus the full manuscript and proposes three distinct ways the author could
 * solve it — an ideation step, not a prescription. The author picks; the agent
 * only widens the option space.
 *
 * Inputs:
 *  - fullStoryContent: the manuscript, so solutions are grounded in the text.
 *  - weakness: the issue to solve (mirrors story-weaknesses-v1's WeaknessIssue).
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// WEAKNESS INPUT SHAPE
// =============================================================================

/**
 * Local mirror of story-weaknesses-v1's WeaknessIssue (minus id/addressed).
 * Duplicated here — rather than imported from the domain schemas — to keep the
 * agent package self-contained.
 */
const WeaknessInputSchema = z.object({
  title: z.string(),
  severity: z.enum(['critical', 'major', 'minor']),
  fixType: z.enum(['structural', 'character', 'content']),
  chapterStart: z.number(),
  chapterEnd: z.number(),
  summary: z.string(),
  detail: z.string(),
}).describe(
  'The diagnosed weakness to solve, as surfaced by story-weaknesses-v1.'
);

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.fullStoryContent.schema.shape,
  weakness: WeaknessInputSchema,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * One proposed way to solve the weakness. The three solutions should be
 * genuinely different routes — not three phrasings of the same fix.
 */
const SolutionSchema = z.object({
  title: z.string().describe(
    'A short, specific label for this approach (max ~8 words). '
    + 'Name the move, e.g. "Plant a quiet betrayal beat in Chapter 4".'
  ),
  summary: z.string().describe(
    'A single-sentence pitch of what this solution does. Plain and concrete.'
  ),
  approach: z.string().describe(
    '1-2 short paragraphs of Markdown explaining how to execute this solution — '
    + 'what changes, where, and how it resolves the weakness. Reference the '
    + 'actual story (characters, chapters, scenes). Concrete enough to act on; '
    + 'do not write the prose itself.'
  ),
  tradeoffs: z.string().describe(
    '1-2 sentences naming what this approach costs or risks — knock-on edits, '
    + 'tonal shifts, what it might weaken elsewhere. Every solution has a cost; '
    + 'name it honestly.'
  ),
  effort: z.enum(['small', 'moderate', 'large']).describe(
    'How much work this solution implies. "small" = a localized edit; '
    + '"moderate" = changes across a few chapters; "large" = structural rework.'
  ),
});

export type Solution = z.infer<typeof SolutionSchema>;

export const OutputSchema = z.object({
  framing: z.string().describe(
    'A 1-2 sentence neutral restatement of the core problem the three solutions '
    + 'are addressing — what specifically needs to change for this weakness to '
    + 'be resolved. Editorial voice.'
  ),
  solutions: z.array(SolutionSchema).length(3).describe(
    'Exactly three distinct solutions, ordered from the lightest-touch approach '
    + 'to the most ambitious. They must be genuinely different routes to fixing '
    + 'the weakness, not variations of one idea.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
